import { createSelector } from 'reselect';
import { Map, List } from 'immutable';
import { getOwnExtensionKey } from 'flow/selectors/selectors';
import { ContactType } from 'flow/utils/extension-utils';
import { getQueueAgentProfileEndTimeExpiringSoon } from 'flow/utils/supervisor-utils';
import { getQueueMembersSoonLoggedInThreshold } from 'flow/selectors/supervisor-selectors';
import { extensionStatusIsInCallOrInExternalCall } from 'flow/utils/extension-utils';
import { getUniqueExtensionKeysForQueueMembersInOpenQueuesProvided } from 'flow/utils/pbx-utils';

import { licenseIsMobile } from 'flow/utils/flow-utils';
import { selectAllQueueMembers } from 'flow/selectors/queue-selectors';
import { getExtensions } from 'flow/selectors/ExtensionSelectors';
import { selectOwnCallRecords } from 'flow/selectors/call-records-selectors';

const getActiveProfiles = (state) => {
    return state.extensions.get('activeProfiles', Map());
};

export const getLoggedInUserExtensionStatus = (state) => {
    const ownExtensionKey = getOwnExtensionKey(state);
    return state.extensions.getIn(['statuses', ownExtensionKey], 'unknown');
};

export const getExtensionsActiveProfiles = (state, queueMembers) => {
    const activeProfiles = getActiveProfiles(state);
    const activeProfileMap = [];
    queueMembers.forEach((member) => {
        const extensionKey = member.get('extensionKey');
        activeProfileMap.push([
            extensionKey,
            activeProfiles.get(extensionKey, Map()),
        ]);
    });
    return Map(activeProfileMap);
};

export const loggedInUserHasOmnichannelFeature = (state) => {
    return !!state.app.getIn(['extension', 'hasOmnichannelFeature']);
};

export const getShouldNotifyPresence = (state, extensionKey) => {
    const loggedInExtensionKey = getOwnExtensionKey(state);
    return state.extensions.hasIn([
        'extensions',
        loggedInExtensionKey,
        'presenceNotifications',
        extensionKey,
    ]);
};

export const getNextScheduledProfiles = (state) =>
    state.extensions.get('nextScheduledProfiles');

/**
 * This method returns the number of extensions provided that meet the following conditions:
 * 1. Will be logged in to one or more of the queues provided, within the soonLoggedInThreshold
 * AND
 * 2. Are not already logged in to any of the queues provided
 */
export const getNumberOfAgentsSoonLoggedInToOneOrManyQueues = createSelector(
    [
        getQueueMembersSoonLoggedInThreshold,
        getExtensions,
        getActiveProfiles,
        getNextScheduledProfiles,
        selectAllQueueMembers,
        (_state, queues) => queues,
    ],
    (
        soonLoggedInThreshold,
        extensions,
        activeProfiles,
        nextScheduledProfiles,
        allQueueMembers,
        queues,
    ) => {
        const agentsSoonLoggedInToOneOrManyQueues = [];
        const extensionKeysForQueueMembersInOpenQueues =
            getUniqueExtensionKeysForQueueMembersInOpenQueuesProvided(
                queues,
                allQueueMembers,
            );

        extensionKeysForQueueMembersInOpenQueues?.forEach((extensionKey) => {
            const extension = extensions.get(extensionKey);

            if (!extension) {
                //Happened with the Alan Jenkins user
                return;
            }

            const activeProfile = activeProfiles.get(extensionKey) || Map();

            if (
                getQueueAgentProfileEndTimeExpiringSoon(
                    activeProfile,
                    soonLoggedInThreshold,
                )
            ) {
                const queuesWhereExtensionIsCurrentlyLoggedIn = queues.filter(
                    (queue) =>
                        allQueueMembers
                            .get(queue.get('key'))
                            ?.find(
                                (member) =>
                                    member.get('loggedIn') === true &&
                                    extension.get('key') ===
                                        member.get('extensionKey'),
                            ),
                );

                if (
                    queuesWhereExtensionIsCurrentlyLoggedIn &&
                    queuesWhereExtensionIsCurrentlyLoggedIn.size > 0
                ) {
                    return;
                }

                const nextScheduledProfile =
                    nextScheduledProfiles?.get(extensionKey);

                if (
                    nextScheduledProfile &&
                    nextScheduledProfile.get('available')
                ) {
                    if (
                        nextScheduledProfileWillLoginUserToAtLeastOneOfQueuesProvided(
                            nextScheduledProfile,
                            queues,
                        )
                    ) {
                        agentsSoonLoggedInToOneOrManyQueues.push(extension);
                    }
                }
            }
        });
        return agentsSoonLoggedInToOneOrManyQueues.length;
    },
);

export const nextScheduledProfileWillLoginUserToAtLeastOneOfQueuesProvided = (
    nextScheduledProfile,
    queues,
) => {
    if (!nextScheduledProfile) {
        return false;
    }

    const queueLoginList = nextScheduledProfile.get('queueLoginList') || Map();
    if (
        queues.find((queue) =>
            queueLoginList.find(
                (queueNext) =>
                    queueNext.get('queueEntityKey') === queue.get('key') &&
                    queueNext.get('loggedIn'),
            ),
        )
    ) {
        return true;
    }

    return false;
};

/**
 * Same algorithm as the mobile apps use to show suggestions.
 * We count the callrecords for each extension and contact
 * then sort it based on the amount.
 */
export const getRecentlyContactedContactsAndExtensions = createSelector(
    [selectOwnCallRecords, getExtensions],
    (callRecords, extensions) => {
        if (!callRecords) return Map();

        const contactTypesToInclude = [
            ContactType.bound.key,
            ContactType.personal.key,
            ContactType.shared.key,
        ];

        let contactsAndExtensions = Map();
        const contactCountMap = callRecords.reduce((countList, callRecord) => {
            const contactType = callRecord.getIn(
                ['contact', 'type'],
                'unknown',
            );
            if (contactTypesToInclude.includes(contactType)) {
                const contactKey = callRecord.getIn(['contact', 'key']);
                const extension = extensions.find(
                    (extension) =>
                        extension.getIn(['contact', 'key']) === contactKey,
                );
                if (extension) {
                    const extensionKey = extension.get('key');
                    contactsAndExtensions = contactsAndExtensions.set(
                        extensionKey,
                        extension,
                    );
                    return countList.update(
                        extensionKey,
                        0,
                        (count) => count + 1,
                    );
                } else {
                    contactsAndExtensions = contactsAndExtensions.set(
                        contactKey,
                        callRecord.get('contact'),
                    );
                    return countList.update(
                        contactKey,
                        0,
                        (count) => count + 1,
                    );
                }
            } else {
                return countList;
            }
        }, Map());

        return contactCountMap
            .sort()
            .reverse()
            .map((_count, key) => contactsAndExtensions.get(key));
    },
);

export const filterExtensionsInCall = (
    extensionStatuses,
    extensionsToFilter,
) => {
    const extensionsInCall = extensionsToFilter.filter((extension) => {
        const key = extension.get('key');
        const status = extensionStatuses.get(key);
        return extensionStatusIsInCallOrInExternalCall(status);
    });

    return extensionsInCall;
};

export const filterExtensionsNotInCall = (
    extensionStatuses,
    extensionsToFilter,
) => {
    const extensionsNotInCall = extensionsToFilter.filter((extension) => {
        const key = extension.get('key');
        const status = extensionStatuses.get(key);
        return !extensionStatusIsInCallOrInExternalCall(status);
    });

    return extensionsNotInCall;
};

export const getMobileSubscription = (state, extensionKey) => {
    return state.extensions.getIn(
        ['extensions', extensionKey, 'mobileSubscription'],
        Map(),
    );
};

export const getMobileExtension = (state, extensionKey) => {
    return state.extensions.getIn(
        ['extensions', extensionKey, 'mobileExtension'],
        Map(),
    );
};

const getOwnMobileSubscription = (state) => {
    const ownExtensionKey = getOwnExtensionKey(state);
    return state.extensions.getIn(
        ['extensions', ownExtensionKey, 'mobileSubscription'],
        Map(),
    );
};

export const getAllSimcards = createSelector(
    [getOwnMobileSubscription],
    (mobileSubscription) => {
        const allSimcardCollections = mobileSubscription.get(
            'simcards',
            List(),
        );
        const allConnectedSimcards = allSimcardCollections.reduce(
            (red, simcard) => {
                return red.concat(
                    simcard
                        .get('connectedSimCards')
                        .filter(
                            (connectedSimcards) => !connectedSimcards.isEmpty(),
                        ),
                );
            },
            List(),
        );
        return allConnectedSimcards;
    },
);

export const getAllSimcardsForExtension = (state, extensionKey) => {
    const allSimcardCollections = state.extensions.getIn(
        ['extensions', extensionKey, 'mobileSubscription', 'simcards'],
        List(),
    );

    return allSimcardCollections.reduce(
        (red, simcard) =>
            red.concat(
                simcard
                    .get('connectedSimCards')
                    .filter(
                        (connectedSimcards) => !connectedSimcards.isEmpty(),
                    ),
            ),
        List(),
    );
};

export const hasMobileLicence = (state, extensionKey) => {
    return licenseIsMobile(
        state.extensions.getIn([
            'extensions',
            extensionKey,
            'license',
            'licenseUsageType',
        ]),
    );
};

export const getExtensionContactFirstNameLastName = (state, extensionKey) => {
    const contact = state.extensions.getIn([
        'extensions',
        extensionKey,
        'contact',
    ]);
    if (!contact) return '';

    return `${contact.get('firstName')} ${contact.get('lastName')}`;
};
